import { useCallback, useContext, useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Modal from "@mui/material/Modal";
import { MasterModalProps, MasteringPresets, navButtons } from "./types";
import { BottomButtonsContainer, GoBackButtonContainer } from "./styles";
import { DetailsContainer, ModalContainer, NavButtons, styles } from "./styles";
import { Details } from "./Details";
import { ChoosePreset } from "./ChoosePreset";
import { Preview } from "./Preview";
import { CreateVersion } from "./CreateVersion";
import Mixpanel from "../../utils/mixpanel";
import { APP_COLOR, TEXT_COLOR } from "../../theme";
import { MasteringBilling } from "./MasteringBilling";
import { CircularProgress } from "@mui/material";
import {
  AuthContext,
  AuthContextInterface,
  UserDetailsContext,
  UserDetailsContextInterface,
} from "../../contexts";
import { fetchMasteredVersions } from "../../services/trackService";
import useGetValidAccessToken from "../../hooks/useGetValidAccessToken";
import { GetUserTrackVersionsResponse } from "../../proto/api_pb";
import { tierMap } from "../../pages/Billing/types";
import {
  MAJOR_MASTERING_ALLOWANCE,
  MINOR_MASTERING_ALLOWANCE,
} from "../constants";
import { toast } from "react-toastify";

export const MasterModal: React.FC<MasterModalProps> = ({
  isMasterModalOpen,
  handleMasterModalOpen,
  handleMasterModalClose,
  track,
  addDolbyEffect,
  masteredMusicUrl,
  handleAddTrackVersion,
  state,
  previewState,
  previewLoading,
  setPreviewLoading,
  previewUrl,
  addNewVersion,
  trackUrl,
  trackVersionId,
  setSelectedPreset,
}) => {
  const [stage, setStage] = useState("Details");
  const [selectedPresetValue, setSelectedPresetValue] = useState("a");
  const [selectedPresetArray, setSelectedPresetArray] = useState<string[]>([
    "a",
  ]);
  const [sliderValue, setSliderValue] = useState(-14);
  const [stageAccess, setStageAccess] = useState(1);
  const [previewStartValue, setPreviewStartValue] = useState<number>(0);
  const [duration, setDuration] = useState<number>(30);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [masteringRemaining, setMasteringRemaining] = useState(0);
  const {
    hasPaymentMethod,
    subscriptionTier,
    enterprise,
    isEnterpriseAccount,
  } = useContext<UserDetailsContextInterface>(UserDetailsContext);
  const { userId } = useContext<AuthContextInterface>(AuthContext);
  const { getValidAccessToken } = useGetValidAccessToken();

  const handleGetMastersRemaining = useCallback(async () => {
    try {
      const token = await getValidAccessToken();
      const response = (await fetchMasteredVersions(
        token,
        userId
      )) as GetUserTrackVersionsResponse;

      const totalVersions = response.getTrackVersionsList().length;
      let remainingMasters = 0;

      if (tierMap[subscriptionTier] === "Minor") {
        remainingMasters = MINOR_MASTERING_ALLOWANCE - totalVersions;
      } else if (tierMap[subscriptionTier] === "Major") {
        remainingMasters = MAJOR_MASTERING_ALLOWANCE - totalVersions;
      }

      // Ensure remaining masters is not less than 0
      remainingMasters = Math.max(remainingMasters, 0);

      setMasteringRemaining(remainingMasters);
    } catch (e) {
      console.log(e);
    }
  }, [getValidAccessToken, userId, subscriptionTier]);

  useEffect(() => {
    const shouldCallFunction =
      !isEnterpriseAccount ||
      (isEnterpriseAccount && enterprise && enterprise?.masteringCount > 25);

    if (shouldCallFunction) {
      handleGetMastersRemaining();
    }
  }, [
    enterprise,
    enterprise?.masteringCount,
    handleGetMastersRemaining,
    isEnterpriseAccount,
  ]);

  const handleAccessStage = (access: number) => {
    if (access >= stageAccess) setStageAccess(access);
  };
  const handleNext = async () => {
    Mixpanel.track(`master_stage_${stage}`);
    if (stage === "Details") {
      setStage("Presets");
    }
    if (stage === "Presets") {
      setPreviewLoading(true);
      addDolbyEffect(
        selectedPresetArray,
        sliderValue,
        trackUrl,
        trackVersionId,
        previewStartValue,
        duration
      );
      setStage("Preview");
    }
    if (stage === "Preview") {
      setPreviewLoading(true);
      handleAddTrackVersion(
        sliderValue,
        selectedPresetValue,
        trackUrl,
        trackVersionId,
        masteringRemaining !== 0
      );
      setStage("Master");
    }
    if (stage === "Master") {
      setStageAccess(1);
      setIsLoading(true);
      await addNewVersion(track);
      setIsLoading(false);
    }
  };
  // const handleBackButton = () => {
  //   if (stage === "Preview") {
  //     setStage("Presets");
  //   } else if (stage === "Presets") {
  //     setStage("Details");
  //   }
  // };

  return (
    <>
      <div>
        {/* <Button onClick={handleMasterModalOpen}>Open modal</Button> */}
        <Modal
          open={isMasterModalOpen}
          onClose={handleMasterModalClose}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
        >
          <ModalContainer>
            <Box sx={styles.headerButtonContainer}>
              {navButtons.map((el, i) => {
                return (
                  <NavButtons
                    key={el}
                    sx={
                      stage === el
                        ? { color: APP_COLOR }
                        : { color: TEXT_COLOR }
                    }
                    disabled={i >= stageAccess || previewLoading}
                    onClick={() => setStage(el)}
                  >
                    {el}
                  </NavButtons>
                );
              })}
            </Box>
            <hr />
            {stage === "Details" && (
              <Details
                track={track}
                setPreviewStartValue={setPreviewStartValue}
                setDuration={setDuration}
              />
            )}
            {stage === "Presets" && (
              <ChoosePreset
                setSelectedPresetArray={setSelectedPresetArray}
                selectedPresetArray={selectedPresetArray}
                selectedPresetValue={selectedPresetValue}
                sliderValue={sliderValue}
                setSliderValue={setSliderValue}
                setLoading={setPreviewLoading}
                handleAccessStage={handleAccessStage}
              />
            )}
            {stage === "Preview" && (
              <Preview
                track={track}
                previewUrl={previewUrl}
                masteredMusicUrl={undefined}
                selectedPresetArray={selectedPresetArray}
                setSelectedPresetValue={setSelectedPresetValue}
                loading={previewLoading}
                previewState={previewState}
                handleAccessStage={handleAccessStage}
                previewStartValue={previewStartValue}
                duration={duration}
                setSelectedPreset={setSelectedPreset}
              />
            )}
            {stage === "Master" && (
              <CreateVersion
                track={track}
                masteredMusicUrl={masteredMusicUrl}
                state={state}
                loading={previewLoading}
                handleAccessStage={handleAccessStage}
              />
            )}
            {stage === "Preview" && !previewLoading && (
              <Typography
                sx={{ color: TEXT_COLOR, mt: 2, justifySelf: "flex-end" }}
              >
                Remaining Masterings: {masteringRemaining}
              </Typography>
            )}
            <BottomButtonsContainer
              sx={{
                justifyContent:
                  stage === "Preview" || stage === "Presets"
                    ? "space-between"
                    : "flex-end",
              }}
            >
              <Box></Box>
              {stage === "Preview" && !previewLoading && (
                <DetailsContainer style={{ color: `${TEXT_COLOR}` }}>
                  <Typography variant="subtitle1" align="center">
                    {`Master with ${
                      MasteringPresets.find(
                        (el) => el.value === selectedPresetValue
                      )?.title
                    } preset`}
                  </Typography>
                  <Typography variant="caption" align="center" marginTop="2px">
                    Clicking &quot;Master&quot; will invoice you for mastering
                  </Typography>

                  <Typography variant="caption" color="grey" align="left">
                    ($3.99/track for Minor Users & $4.99/track for Major Users)
                  </Typography>
                </DetailsContainer>
              )}
              <GoBackButtonContainer>
                <Button
                  onClick={() => {
                    if (
                      stage === "Presets" &&
                      selectedPresetArray.length === 0
                    ) {
                      toast.warn("Please select at least one preset");
                      console.log(100);
                    } else {
                      handleNext();
                    }
                  }}
                  variant="contained"
                  disabled={
                    (stage === "Preview" || stage === "Master") &&
                    previewLoading
                  }
                  sx={styles.stageButton}
                >
                  {stage === "Details" && !isLoading ? "Next" : ""}
                  {stage === "Presets" && !isLoading ? "Preview" : ""}
                  {stage === "Preview" && !isLoading ? "Master" : ""}
                  {stage === "Master" && !isLoading ? "Save" : ""}
                  {isLoading && (
                    <CircularProgress
                      sx={{ color: "black", fontSize: "14px" }}
                    />
                  )}
                </Button>
              </GoBackButtonContainer>
            </BottomButtonsContainer>
          </ModalContainer>
        </Modal>
      </div>
      {!hasPaymentMethod
        ? true
        : enterprise &&
          enterprise?.masteringCount > 25 && (
            <MasteringBilling
              handleMasterModalOpen={handleMasterModalOpen}
              isMasterModalOpen={isMasterModalOpen}
              handleMasterModalClose={handleMasterModalClose}
            />
          )}
    </>
  );
};
